import logger from 'ws-scripts/modules/logger';
import { addNewRelicPageAction } from '@ddc/new-relic';
import { publishEvent } from '../utilities/pubsub';
import {
	isValidPostalCode,
	parseRange,
	parseExcludeByRange
} from '../utilities/locationUtils';
import { getDefaultPostalCode } from '../utilities/defaultPostalCode';
import { getDDCPubSub } from '../utilities/ddcHelper';
import { LocationFilterManager } from './LocationFilterManager';

// Configuration Constants
const DEFAULT_RANGE = 50;
const DEFAULT_EXCLUDE_BY_RANGE = true;
const WIDGET_NAME = 'ws-locations-data';
const TOPIC_LOCATIONS = 'locations';
const TOPIC_LOCATION_SELECTED = 'location-selected';
const TOPIC_RADIUS_CHANGED = 'radius-changed';
const TOPIC_FILTERED_LOCATIONS = 'filtered-locations';
const TOPIC_RESET_MAKE_FILTER = 'reset-make-filter';

// Subscription labels used in _createSubscription log messages
const LABEL_LOCATIONS = 'locations';
const LABEL_LOCATION_SELECTED = 'location selected';
const LABEL_FILTERED_LOCATIONS = 'filtered locations';
const LABEL_RESET_MAKE_FILTER = 'reset make filter';
const LABEL_RADIUS_CHANGED = 'radius changed';

const LOG_PREFIX = `[${WIDGET_NAME}][LocationsData]`;

export class LocationsData {
	constructor(requestHandler, { defaultPostalCode } = {}) {
		this.requestHandler = requestHandler;
		this.locations = [];
		this.postalCode = '';
		this.selectedLocationCoordinates = null;
		this.range = DEFAULT_RANGE;
		this.excludeByRange = DEFAULT_EXCLUDE_BY_RANGE;
		this.isDefaultView = true;
		this.defaultPostalCode = getDefaultPostalCode({ defaultPostalCode });
		this.error = null;
		this.TOPIC_PREFIX = WIDGET_NAME;
		this.LOCATIONS_TOPIC = `${this.TOPIC_PREFIX}/${TOPIC_LOCATIONS}`;
		this.LOCATION_SELECTED_TOPIC = `${this.TOPIC_PREFIX}/${TOPIC_LOCATION_SELECTED}`;
		this.RADIUS_CHANGED_TOPIC = `${this.TOPIC_PREFIX}/${TOPIC_RADIUS_CHANGED}`;
		this.FILTERED_LOCATIONS_TOPIC = `${this.TOPIC_PREFIX}/${TOPIC_FILTERED_LOCATIONS}`;
		this.RESET_MAKE_FILTER_TOPIC = `${this.TOPIC_PREFIX}/${TOPIC_RESET_MAKE_FILTER}`;

		// Internal filter manager
		this._filterManager = new LocationFilterManager({
			getLocations: () => this.locations,
			publishFilteredLocations: () =>
				publishEvent(this.FILTERED_LOCATIONS_TOPIC),
			publishResetMakeFilter: () =>
				publishEvent(this.RESET_MAKE_FILTER_TOPIC)
		});

		logger.debug(`${LOG_PREFIX} Constructor called`);

		// Self-subscribe to LOCATIONS_TOPIC so filters are re-applied
		// automatically whenever new location data arrives.
		this.subscribeEvent(this.LOCATIONS_TOPIC, () => {
			this.reapplyFilters();
		});
	}

	/**
	 * Proxy getter/setter so that direct access to `this.filters`
	 */
	get filters() {
		return this._filterManager
			? this._filterManager.filters
			: { makes: [] };
	}

	/**
	 * Private helper: subscribes `callback` to `topic` and returns an
	 * unsubscribe function.
	 */
	_createSubscription(topic, callback, label) {
		logger.debug(`${LOG_PREFIX} Registering ${label} subscriber`, {
			topic
		});
		this.subscribeEvent(topic, callback);
		return () => {
			logger.debug(`${LOG_PREFIX} Unsubscribing from ${label}`, {
				topic
			});
			this.unsubscribeEvent(topic, callback);
		};
	}

	// Register callback for location updates
	registerLocationsNotification(callback) {
		return this._createSubscription(
			this.LOCATIONS_TOPIC,
			callback,
			LABEL_LOCATIONS
		);
	}

	// Register callback for location selection events
	registerLocationSelectedNotification(callback) {
		return this._createSubscription(
			this.LOCATION_SELECTED_TOPIC,
			callback,
			LABEL_LOCATION_SELECTED
		);
	}

	// Get all locations
	getLocations() {
		return this.locations;
	}

	// Get current selected location coordinates
	getSelectedLocationCoordinates() {
		return this.selectedLocationCoordinates;
	}

	// Get error message if any
	getError() {
		return this.error;
	}

	getPostalCode() {
		return this.postalCode || this.defaultPostalCode;
	}

	getDefaultPostalCode() {
		return this.defaultPostalCode;
	}

	getDefaultViewState() {
		return this.isDefaultView;
	}

	/**
	 * Registers a callback to be invoked whenever the search radius changes
	 * inside setSearchParams(). Used by ws-locations-search and ws-locations-facets
	 * to keep their UI in sync. Returns an unsubscribe function.
	 */
	registerRadiusChangedNotification(callback) {
		return this._createSubscription(
			this.RADIUS_CHANGED_TOPIC,
			callback,
			LABEL_RADIUS_CHANGED
		);
	}

	/** Returns the current search radius in miles. */
	getRange() {
		return this.range;
	}

	getActiveMakes() {
		return this._filterManager.getActiveMakes();
	}

	setFilters(filters = {}) {
		this._filterManager.setFilters(filters);
	}

	hasActiveFilters() {
		return this._filterManager.hasActiveFilters();
	}

	getFilteredLocations() {
		return this._filterManager.getFilteredLocations();
	}

	getMakeFacets() {
		return this._filterManager.getMakeFacets();
	}

	reapplyFilters() {
		this._filterManager.reapplyFilters();
	}

	registerFilteredLocationsNotification(callback) {
		return this._createSubscription(
			this.FILTERED_LOCATIONS_TOPIC,
			callback,
			LABEL_FILTERED_LOCATIONS
		);
	}

	/**
	 * Immediately clears all active Make filters and publishes the
	 * reset-make-filter topic so that ws-locations-facets can sync its
	 * Redux state. Called by ws-locations-control when the search is cleared.
	 */
	resetMakeFilter() {
		this._filterManager.resetMakeFilter();
	}

	/**
	 * Registers a callback to be invoked whenever resetMakeFilter() is
	 * called. Used by ws-locations-facets to keep its Redux selectedMakes
	 * in sync. Returns an unsubscribe function.
	 */
	registerResetMakeFilterNotification(callback) {
		return this._createSubscription(
			this.RESET_MAKE_FILTER_TOPIC,
			callback,
			LABEL_RESET_MAKE_FILTER
		);
	}

	/*
	 * Set search parameters and trigger location fetch
	 * Validates postal code and only triggers fetch if parameters have changed
	 */
	setSearchParams(postalCode, range, excludeByRange, isDefaultView = false) {
		let postalCodeToUse = postalCode;
		if (isDefaultView) {
			postalCodeToUse = this.defaultPostalCode;
		}
		if (!isValidPostalCode(postalCodeToUse)) {
			logger.error(`${LOG_PREFIX} Invalid postal code:`, postalCodeToUse);

			// Track invalid postal code submission in New Relic
			addNewRelicPageAction('WS LOCATIONS DATA INVALID POSTAL CODE', {
				locationsPostalCode: postalCodeToUse,
				locationsRange: range
			});

			this.locations = [];
			this.error = 'Invalid postal code';
			publishEvent(this.LOCATIONS_TOPIC);
			return;
		}
		const trimmedPostalCode = postalCodeToUse.trim();
		const newRange = parseRange(range, this.range);
		const newExcludeByRange = parseExcludeByRange(
			excludeByRange,
			this.excludeByRange
		);

		const hasChanged =
			isDefaultView !== this.isDefaultView ||
			trimmedPostalCode !== this.postalCode ||
			newRange !== this.range ||
			newExcludeByRange !== this.excludeByRange;

		if (!hasChanged) {
			logger.debug(`${LOG_PREFIX} No changes detected, skipping fetch`);
			return;
		}
		const rangeChanged = newRange !== this.range;
		this.isDefaultView = isDefaultView;
		this.postalCode = trimmedPostalCode;
		this.range = newRange;
		this.excludeByRange = newExcludeByRange;

		logger.debug(`${LOG_PREFIX} Parameters updated:`, {
			postalCode: this.postalCode,
			range: this.range,
			excludeByRange: this.excludeByRange
		});

		// Notify all widgets that the radius changed so they can sync their UI
		if (rangeChanged) {
			publishEvent(this.RADIUS_CHANGED_TOPIC, { radius: newRange });

			// Persist to sessionStorage so the radius survives page refreshes
			try {
				sessionStorage.setItem(
					'ws-locations-facets-radius',
					String(newRange)
				);
			} catch (e) {
				logger.warn(
					`${LOG_PREFIX} Failed to persist radius to sessionStorage`
				);
			}
		}

		this._fetchLocations();
	}

	// Set location with coordinates
	setLocationWithCoordinates({ coordinates }) {
		logger.info(`${LOG_PREFIX} Setting location with coordinates`, {
			coordinates
		});

		// Store selected location coordinates
		this.selectedLocationCoordinates = coordinates;

		// Publish location selected event
		publishEvent(this.LOCATION_SELECTED_TOPIC, {
			coordinates
		});
	}

	// Trigger location fetch
	_fetchLocations() {
		logger.debug(`${LOG_PREFIX} Triggering fetch`, {
			postalCode: this.postalCode,
			range: this.range,
			excludeByRange: this.excludeByRange
		});

		this.requestHandler({
			postalCode: this.postalCode,
			range: this.range,
			excludeByRange: this.excludeByRange,
			useDefaultTileCount:
				this.isDefaultView && this.range === DEFAULT_RANGE
		});
	}

	// eslint-disable-next-line class-methods-use-this
	subscribeEvent(topic, callback) {
		const pubsub = getDDCPubSub();
		if (pubsub) {
			logger.debug(`${LOG_PREFIX} Subscribing`, { topic });
			pubsub.subscribe(topic, callback);

			const subscribers = pubsub.subscribers?.[topic];
			logger.debug(`${LOG_PREFIX} Subscribed`, {
				topic,
				totalSubscribers: subscribers?.length || 0
			});
		} else {
			logger.warn(`${LOG_PREFIX} pubsub not available`, { topic });
		}
	}

	// Unsubscribe from event through PubSub
	// eslint-disable-next-line class-methods-use-this
	unsubscribeEvent(topic, callback) {
		const pubsub = getDDCPubSub();
		if (pubsub) {
			logger.debug(`${LOG_PREFIX} Unsubscribing`, { topic });
			pubsub.unsubscribe(topic, callback);

			const subscribers = pubsub.subscribers?.[topic];
			logger.debug(`${LOG_PREFIX} Unsubscribed`, {
				topic,
				totalSubscribers: subscribers?.length || 0
			});
		} else {
			logger.warn(`${LOG_PREFIX} pubsub not available for unsubscribe`, {
				topic
			});
		}
	}
}
